import { AlertCircle, ArrowLeft, BookOpen, CheckCircle, Copy, Target } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { loadExample, loadExamples } from '../lib/contentLoader';
import { processMarkdown } from '../lib/content';
import { audienceLabels, complexityLabels, goalLabels } from '../lib/router';
import { Example } from '../types/content';
import { RelatedExamples } from '../components/RelatedExamples';

export function ExamplePage() {
  const { slug } = useParams<{ slug: string }>();
  const [example, setExample] = useState<Example | null>(null);
  const [allExamples, setAllExamples] = useState<Example[]>([]);
  const [processedContent, setProcessedContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedTemplate, setCopiedTemplate] = useState(false);

  useEffect(() => {
    if (!slug) return;
    
    const loadExampleData = async () => {
      try {
        setLoading(true);
        const [exampleData, examples] = await Promise.all([
          loadExample(slug),
          loadExamples()
        ]);
        
        setExample(exampleData);
        setAllExamples(examples);
        
        if (!exampleData) {
          setError('Example not found');
        } else if (exampleData.content) {
          // Process markdown content to HTML
          const html = await processMarkdown(exampleData.content);
          setProcessedContent(html);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load example');
      } finally {
        setLoading(false);
      }
    };

    loadExampleData();
  }, [slug]);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedTemplate(true);
      setTimeout(() => setCopiedTemplate(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  if (loading) {
    return (
      <div className="container py-16">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-muted rounded w-3/4 mb-4" />
            <div className="h-4 bg-muted rounded w-1/2 mb-8" />
            <div className="space-y-3">
              <div className="h-4 bg-muted rounded" />
              <div className="h-4 bg-muted rounded w-5/6" />
              <div className="h-4 bg-muted rounded w-4/5" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !example) {
    return (
      <>
        <Helmet>
          <title>Example Not Found - Promptliteracy.ai</title>
        </Helmet>
        
        <div className="container py-16 text-center">
          <h1 className="text-2xl font-bold mb-4">Example Not Found</h1>
          <p className="text-muted-foreground mb-8">{error || 'This example could not be found.'}</p>
            <Button variant="outline" size="sm" asChild className="border-slate-300 btn-blue-hover transition-colors">
              <Link to="/examples">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Examples
              </Link>
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{example.title} - Promptliteracy.ai</title>
        <meta name="description" content={example.context} />
        <meta property="og:title" content={example.title} />
        <meta property="og:description" content={example.context} />
        <meta property="og:type" content="article" />
      </Helmet>

      <div className="container py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          <div className="lg:col-span-3">
        {/* Breadcrumb */}
        <nav className="mb-6 text-sm">
          <Link to="/" className="text-muted-foreground link-underline">
            Home
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to="/examples" className="text-muted-foreground link-underline">
            Examples
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground font-medium">{example.title}</span>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-1 mb-4">
            <Link to={`/tag/${example.complexity}`} className="text-sm text-primary hover:text-primary/80 link-underline cursor-pointer">
              {complexityLabels[example.complexity]}
            </Link>
            <span className="text-muted-foreground mx-2">|</span>
            <Link to={`/tag/${example.audience}`} className="text-sm text-secondary hover:text-primary link-underline cursor-pointer">
              {audienceLabels[example.audience]}
            </Link>
            <span className="text-muted-foreground mx-2">|</span>
            <Link to={`/tag/${example.goal}`} className="text-sm text-primary hover:text-primary/80 link-underline cursor-pointer">
              {goalLabels[example.goal]}
            </Link>
            {example.tags && example.tags.length > 0 && (
              <>
                <span className="text-muted-foreground mx-2">|</span>
                {example.tags.map((tag, index) => (
                  <span key={tag}>
                    <Link 
                      to={`/tag/${tag}`}
                      className="text-sm text-secondary hover:text-primary link-underline cursor-pointer"
                    >
                      {tag.replace('-', ' ')}
                    </Link>
                    {index < example.tags.length - 1 && (
                      <span className="text-muted-foreground mx-2">|</span>
                    )}
                  </span>
                ))}
              </>
            )}
          </div>
          
          <h1 className="text-4xl font-bold mb-4 leading-tight">{example.title}</h1>
          <p className="text-xl text-muted-foreground leading-relaxed">
            {example.context}
          </p>
        </div>

        {/* Prompt Template */}
        <Card className="mb-8 card-accent">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Prompt Template
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <pre className="bg-muted/50 rounded-lg p-4 text-sm overflow-x-auto whitespace-pre-wrap">
                {example.prompt_template}
              </pre>
              <Button
                variant="outline"
                size="sm"
                className="border-slate-300 btn-blue-hover absolute top-2 right-2"
                onClick={() => copyToClipboard(example.prompt_template)}
              >
                {copiedTemplate ? (
                  <CheckCircle className="h-4 w-4 text-white-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
                {copiedTemplate ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Variables */}
        {example.variables.length > 0 && (
          <Card className="mb-8 card-accent">
            <CardHeader>
              <CardTitle>Variables to Replace</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {example.variables.map((variable, index) => (
                  <div key={index} className="border rounded-lg p-3">
                    <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
                      {`{{${variable.name}}}`}
                    </code>
                    <p className="text-sm text-muted-foreground mt-2">
                      {variable.description}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step-by-Step Instructions */}
        <Card className="mb-8 card-accent">
          <CardHeader>
            <CardTitle>Step-by-Step Instructions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {example.steps.map((step, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">{index + 1}</span>
                  </div>
                  <p className="text-sm">{step}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Why It Works */}
        <Card className="mb-8 border-green-200 bg-green-50/50 card-accent">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-800">
              <CheckCircle className="h-5 w-5" />
              Why This Works
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-green-700">{example.why_it_works}</p>
          </CardContent>
        </Card>

        {/* Failure Modes */}
        {example.failure_modes.length > 0 && (
          <Card className="mb-8 border-orange-200 bg-orange-50/50 card-accent">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-800">
                <AlertCircle className="h-5 w-5" />
                Common Issues & Solutions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {example.failure_modes.map((mode, index) => (
                  <div key={index} className="border-l-4 border-orange-300 pl-4">
                    <p className="font-medium text-orange-800 mb-1">
                      Problem: {mode.problem}
                    </p>
                    <p className="text-orange-700 text-sm">
                      Solution: {mode.solution}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Full Content */}
        {example.content && processedContent && (
          <Card className="mb-8 card-accent">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Detailed Guide
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose max-w-none">
                <div dangerouslySetInnerHTML={{ __html: processedContent }} />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Citations */}
        {example.citations.length > 0 && (
          <Card className="mb-8 card-accent">
            <CardHeader>
              <CardTitle>References</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {example.citations.map((citation, index) => (
                  <div key={index}>
                    <a
                      href={citation.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline text-sm"
                    >
                      {citation.title}
                    </a>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

          </div>
          
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <RelatedExamples currentExample={example} allExamples={allExamples} />
          </div>
        </div>
      </div>
    </>
  );
}